import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

/**
 * RnD-C — AI Data Flow panel.
 *
 * Read-only audit surface for the per-surface AI data-flow contract.
 * Two views:
 *   - Summary: features × payload classes, count + total bytes over 30 days.
 *   - Recent:  last 100 classified LLM calls, filterable by feature/class.
 *
 * No PII is displayed — the backend strips user_email from every entry.
 */
export default function AIDataFlowPanel({ authHeaders }) {
  const [view, setView] = useState("summary");

  const headers = useCallback(() => {
    if (typeof authHeaders === "function") return authHeaders();
    const email =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    return { "X-Bonsai-User-Id": email };
  }, [authHeaders]);

  return (
    <section
      data-testid="ai-dataflow-panel"
      className="rounded-xl border border-white/[0.07] bg-navy-card p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-100">AI Data Flow</h2>
        <div className="flex gap-1" role="tablist">
          <ViewTab active={view === "summary"} onClick={() => setView("summary")} testid="ai-dataflow-tab-summary">
            Summary
          </ViewTab>
          <ViewTab active={view === "recent"} onClick={() => setView("recent")} testid="ai-dataflow-tab-recent">
            Recent calls
          </ViewTab>
        </div>
      </div>

      {view === "summary" ? (
        <SummaryView headers={headers} />
      ) : (
        <RecentView headers={headers} />
      )}
    </section>
  );
}

function ViewTab({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={
        "px-2.5 py-1 rounded text-xs font-medium border " +
        (active
          ? "bg-slate-700 text-slate-100 border-slate-600"
          : "bg-slate-800 text-slate-300 border-slate-700/50 hover:bg-slate-700/70")
      }
    >
      {children}
    </button>
  );
}

function SummaryView({ headers }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/compliance/ai-call-summary`, {
        headers: headers(),
      });
      const data = res.data || {};
      setRows(Array.isArray(data.summary) ? data.summary : []);
      setMeta({ window_days: data.window_days, total_calls: data.total_calls });
    } catch (err) {
      setError(errorMessage(err, "request failed"));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading && rows.length === 0) {
    return <Skeleton testid="ai-dataflow-summary-skeleton" />;
  }
  if (error) {
    return <ErrorBox testid="ai-dataflow-summary-error" onRetry={fetchSummary} />;
  }

  return (
    <div className="space-y-2" data-testid="ai-dataflow-summary">
      <p className="text-xs text-slate-400">
        {meta ? `Last ${meta.window_days} days · ${meta.total_calls} classified calls` : ""}
      </p>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="ai-dataflow-summary-empty">
          No AI calls recorded in the window.
        </div>
      ) : (
        <table className="w-full text-sm text-slate-200" data-testid="ai-dataflow-summary-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-400">
              <th className="text-left py-1 pr-3">Feature</th>
              <th className="text-left py-1 pr-3">Payload class</th>
              <th className="text-right py-1 pr-3">Calls</th>
              <th className="text-right py-1 pr-3">Total bytes</th>
              <th className="text-right py-1">Errors</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={`${r.feature}-${r.payload_class}`}
                data-testid="ai-dataflow-summary-row"
                className="border-t border-slate-800/60"
              >
                <td className="py-1 pr-3">{r.feature}</td>
                <td className="py-1 pr-3">
                  <PayloadBadge value={r.payload_class} />
                </td>
                <td className="py-1 pr-3 text-right">{r.count}</td>
                <td className="py-1 pr-3 text-right font-mono text-xs">{formatBytes(r.total_bytes)}</td>
                <td className="py-1 text-right">{r.error_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RecentView({ headers }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feature, setFeature] = useState("");
  const [payloadClass, setPayloadClass] = useState("");

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (feature.trim()) params.set("feature", feature.trim());
      if (payloadClass) params.set("payload_class", payloadClass);
      const res = await axios.get(
        `${API}/compliance/ai-call-log?${params.toString()}`,
        { headers: headers() }
      );
      const data = res.data || {};
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch (err) {
      setError(errorMessage(err, "request failed"));
    } finally {
      setLoading(false);
    }
  }, [headers, feature, payloadClass]);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  return (
    <div className="space-y-2" data-testid="ai-dataflow-recent">
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="text"
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
          placeholder="Filter by feature…"
          data-testid="ai-dataflow-recent-feature"
          className="px-2 py-1 rounded text-xs bg-slate-800 text-slate-100 border border-slate-700/60 placeholder-slate-500"
        />
        <select
          value={payloadClass}
          onChange={(e) => setPayloadClass(e.target.value)}
          data-testid="ai-dataflow-recent-class"
          className="px-2 py-1 rounded text-xs bg-slate-800 text-slate-100 border border-slate-700/60"
        >
          <option value="">All classes</option>
          <option value="metadata_only">metadata_only</option>
          <option value="bounded_preview">bounded_preview</option>
          <option value="full_body">full_body</option>
          <option value="structured_only">structured_only</option>
          <option value="unclassified">unclassified</option>
        </select>
      </div>

      {loading && entries.length === 0 ? (
        <Skeleton testid="ai-dataflow-recent-skeleton" />
      ) : error ? (
        <ErrorBox testid="ai-dataflow-recent-error" onRetry={fetchRecent} />
      ) : entries.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="ai-dataflow-recent-empty">
          No matching AI calls.
        </div>
      ) : (
        <table className="w-full text-sm text-slate-200" data-testid="ai-dataflow-recent-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-400">
              <th className="text-left py-1 pr-3">When</th>
              <th className="text-left py-1 pr-3">Feature</th>
              <th className="text-left py-1 pr-3">Class</th>
              <th className="text-left py-1 pr-3">Model</th>
              <th className="text-right py-1 pr-3">Bytes</th>
              <th className="text-right py-1 pr-3">Latency</th>
              <th className="text-right py-1">OK</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr
                key={e.request_id || `${e.feature}-${e.timestamp}`}
                data-testid="ai-dataflow-recent-row"
                className="border-t border-slate-800/60"
              >
                <td className="py-1 pr-3" title={e.timestamp}>{formatRelative(e.timestamp)}</td>
                <td className="py-1 pr-3">{e.feature}</td>
                <td className="py-1 pr-3"><PayloadBadge value={e.payload_class} /></td>
                <td className="py-1 pr-3 font-mono text-xs">{e.model || "—"}</td>
                <td className="py-1 pr-3 text-right font-mono text-xs">{formatBytes(e.bytes_sent)}</td>
                <td className="py-1 pr-3 text-right">{e.latency_ms != null ? `${e.latency_ms}ms` : "—"}</td>
                <td className="py-1 text-right">{e.success ? "yes" : "no"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PayloadBadge({ value }) {
  const cls =
    {
      metadata_only: "bg-emerald-900/40 text-emerald-200 border-emerald-800/50",
      bounded_preview: "bg-sky-900/40 text-sky-200 border-sky-800/50",
      structured_only: "bg-indigo-900/40 text-indigo-200 border-indigo-800/50",
      full_body: "bg-amber-900/40 text-amber-200 border-amber-800/50",
      unclassified: "bg-red-900/40 text-red-200 border-red-800/50",
    }[value] || "bg-slate-800 text-slate-300 border-slate-700/50";
  return (
    <span className={"px-1.5 py-0.5 rounded text-xs font-mono border " + cls}>
      {value || "—"}
    </span>
  );
}

function Skeleton({ testid }) {
  return (
    <div data-testid={testid} className="h-4 w-44 bg-slate-800 rounded animate-pulse" />
  );
}

function ErrorBox({ testid, onRetry }) {
  return (
    <div className="rounded-md border border-red-800/50 bg-red-950/30 p-3 text-sm text-red-200">
      <span data-testid={testid}>AI data-flow data unavailable. Try reloading.</span>{" "}
      <button
        type="button"
        onClick={onRetry}
        className="ml-2 px-2 py-0.5 rounded border border-red-700/60 hover:bg-red-900/30"
      >
        Retry
      </button>
    </div>
  );
}

function formatBytes(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const b = Number(n);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
